import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { getAllCombinations } from 'utils/supabase-feed';
import interiorOptions from 'utils/data/interiorOptions';
import { useState, useCallback } from 'react';

// Custom component to handle style images with fallbacks
function CustomStyleImage({ styleKey, altText }) {
  const [imgSrc, setImgSrc] = useState(`/iso${styleKey}.png`);
  const [loading, setLoading] = useState(true);
  
  // Fallback handler
  const handleError = useCallback(() => {
    // Generate a fallback image if the original fails to load
    const fallbacks = ['/isomodern.webp', '/isoindustrial.png', '/isoscandi.png', '/isoeclectic.png'];
    const randomFallback = fallbacks[Math.floor(Math.random() * fallbacks.length)];
    setImgSrc(randomFallback);
  }, []);

  return (
    <Image 
      src={imgSrc}
      alt={altText}
      fill
      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
      className={`object-cover transition-transform duration-300 hover:scale-105 ${loading ? 'animate-pulse' : ''}`}
      onLoadingComplete={() => setLoading(false)}
      onError={handleError}
    />
  );
}

export default function AllRooms({ combinations }) {
  const { t } = useTranslation('common');
  const [activeRoomFilter, setActiveRoomFilter] = useState(null);
  const [activeStyleFilter, setActiveStyleFilter] = useState(null);
  
  // Get all unique room types from the combinations
  const roomTypes = [...new Set(combinations.map(combo => combo.room_type))].sort();
  
  // Get all unique styles from the combinations
  const styles = [...new Set(combinations.map(combo => combo.style))].sort();
  
  // Format a title for display
  function formatTitle(text) {
    return text.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }
  
  // Format slug for URL
  function formatSlug(roomType, style) {
    return `${roomType.toLowerCase().replace(/ /g, '_')}-ideas-${style.toLowerCase().replace(/ /g, '_')}`;
  }
  
  // Filter combinations based on active filters
  const filteredCombinations = combinations.filter(combo => {
    if (activeRoomFilter && combo.room_type !== activeRoomFilter) return false;
    if (activeStyleFilter && combo.style !== activeStyleFilter) return false;
    return true;
  });
  
  // Group filtered combinations by room type for display
  const groupedCombinations = filteredCombinations.reduce((acc, combo) => {
    if (!acc[combo.room_type]) {
      acc[combo.room_type] = [];
    }
    acc[combo.room_type].push(combo);
    return acc;
  }, {});
  
  // Sort each room's styles by popularity (based on the order in the combinations array)
  Object.keys(groupedCombinations).forEach(roomType => {
    groupedCombinations[roomType].sort((a, b) => {
      const aCount = combinations.filter(c => c.room_type === a.room_type && c.style === a.style).length;
      const bCount = combinations.filter(c => c.room_type === b.room_type && c.style === b.style).length;
      return bCount - aCount;
    });
  });
  
  // Popular room-style combinations to feature
  const popularCombinations = [
    { room_type: "Living Room", style: "italian", display: "Italian Living Room" },
    { room_type: "Kitchen", style: "italian", display: "Italian Kitchen" },
    { room_type: "Living Room", style: "industrial", display: "Industrial Living Room" },
    { room_type: "Living Room", style: "eclectic", display: "Eclectic Living Room" },
    { room_type: "Bedroom", style: "italian", display: "Italian Bedroom" },
    { room_type: "Living Room", style: "modern", display: "Modern Living Room" }
  ];
  
  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto px-4 mb-16 mt-12">
      <Head>
        <title>Room Design Ideas & Inspirations | SofaBrain</title>
        <meta
          content="Explore room design ideas with SofaBrain. Find inspiration for your living room, bedroom, kitchen, bathroom and more with our comprehensive room design guide."
          name="description"
        />
      </Head>
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-purple-900 to-indigo-800 p-10 md:p-16 rounded-3xl text-white mb-12">
        <div className="absolute inset-0 bg-black opacity-20 mix-blend-overlay"></div>
        <div className="relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Room Design Inspirations</h1>
          <p className="text-xl max-w-3xl">
            Browse our collection of room-style combinations to find the perfect inspiration for every space in your home.
          </p>
        </div>
      </div>
      
      {/* Filter Controls */}
      <div className="sticky top-0 z-20 bg-white bg-opacity-95 backdrop-blur-md p-4 rounded-xl shadow-md mb-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold mb-2">Filter Combinations</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="mr-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
              <select 
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                value={activeRoomFilter || ''}
                onChange={(e) => setActiveRoomFilter(e.target.value || null)}
              >
                <option value="">All Rooms</option>
                {roomTypes.map(room => (
                  <option key={room} value={room}>{formatTitle(room)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Style</label>
              <select 
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                value={activeStyleFilter || ''}
                onChange={(e) => setActiveStyleFilter(e.target.value || null)}
              >
                <option value="">All Styles</option>
                {styles.map(style => (
                  <option key={style} value={style}>{formatTitle(style)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {/* Active Filters */}
        {(activeRoomFilter || activeStyleFilter) && (
          <div className="flex items-center mt-4">
            <span className="text-sm text-gray-600 mr-2">Active Filters:</span>
            <div className="flex flex-wrap gap-2">
              {activeRoomFilter && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {formatTitle(activeRoomFilter)}
                  <button 
                    onClick={() => setActiveRoomFilter(null)}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-200 text-indigo-600 hover:bg-indigo-300"
                  >
                    ×
                  </button>
                </span>
              )}
              {activeStyleFilter && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  {formatTitle(activeStyleFilter)}
                  <button 
                    onClick={() => setActiveStyleFilter(null)}
                    className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-purple-200 text-purple-600 hover:bg-purple-300"
                  >
                    ×
                  </button>
                </span>
              )}
              {(activeRoomFilter || activeStyleFilter) && (
                <button 
                  onClick={() => {
                    setActiveRoomFilter(null);
                    setActiveStyleFilter(null);
                  }}
                  className="text-xs text-gray-500 hover:text-gray-800 underline ml-2"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Featured Combinations Section */}
      {!activeRoomFilter && !activeStyleFilter && (
        <div className="mb-16">
          <h2 className="text-2xl font-bold mb-6">Popular Room Designs</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {popularCombinations.map((combo) => (
              <Link 
                key={`${combo.room_type}-${combo.style}`} 
                href={`/inspiration/rooms/${formatSlug(combo.room_type, combo.style)}`}
                className="group bg-white hover:bg-gray-50 border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all transform hover:-translate-y-1"
              >
                <div className="relative h-32 overflow-hidden">
                  <div className="relative w-full h-full">
                    <CustomStyleImage 
                      styleKey={combo.style.toLowerCase().replace(/ /g, '')}
                      altText={combo.display}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent opacity-50"></div>
                  <div className="absolute top-2 right-2">
                    <span className="bg-white/80 backdrop-blur-sm text-xs font-medium text-gray-800 px-2 py-1 rounded-full">
                      {formatTitle(combo.style)}
                    </span>
                  </div>
                </div>
                <div className="p-4 text-center">
                  <h3 className="font-semibold text-lg mb-1">{combo.display}</h3>
                  <p className="text-gray-500 text-sm">View designs</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {/* All Room Combinations */}
      <div>
        <h2 className="text-2xl font-bold mb-8">
          {activeRoomFilter 
            ? `${formatTitle(activeRoomFilter)} Designs` 
            : activeStyleFilter 
              ? `${formatTitle(activeStyleFilter)} Room Designs` 
              : 'All Room Designs'
          }
        </h2>
        
        {Object.keys(groupedCombinations).length > 0 ? (
          Object.keys(groupedCombinations).sort().map(roomType => (
            <div key={roomType} className="mb-12">
              <div className="flex items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">{formatTitle(roomType)}</h3>
                <div className="h-px bg-gray-200 flex-grow ml-4"></div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {groupedCombinations[roomType].map(combo => (
                  <Link 
                    key={`${combo.room_type}-${combo.style}`} 
                    href={`/inspiration/rooms/${formatSlug(combo.room_type, combo.style)}`}
                    className="flex flex-col bg-white hover:bg-gray-50 border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="h-40 relative overflow-hidden">
                      <div className="relative w-full h-full">
                        <CustomStyleImage 
                          styleKey={combo.style.toLowerCase().replace(/ /g, '')}
                          altText={`${formatTitle(combo.room_type)} in ${formatTitle(combo.style)} Style`}
                        />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent h-1/2"></div>
                      <div className="absolute bottom-2 right-2">
                        <span className="text-xs bg-white/80 backdrop-blur-sm text-gray-800 px-2 py-1 rounded-full font-medium">
                          {formatTitle(combo.style)}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold">{formatTitle(combo.room_type)} in {formatTitle(combo.style)} Style</h4>
                      <p className="text-gray-500 text-sm mt-1">View design ideas</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-xl">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-700">No combinations found</h3>
            <p className="text-gray-500 mt-2">Try changing your filters to see more options</p>
          </div>
        )}
      </div>
      
      {/* Call to Action */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-8 rounded-xl mt-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Ready to transform your space?</h2>
        <p className="mb-6 max-w-2xl mx-auto">
          Upload a photo of your room and see it instantly transformed in any style with SofaBrain's AI-powered interior design tool.
        </p>
        <Link 
          href="/"
          className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-6 py-3 rounded-lg transition-colors"
        >
          Try SofaBrain Now
        </Link>
      </div>
    </div>
  );
}

export async function getStaticProps({ locale }) {
  const combinations = await getAllCombinations();
  
  return {
    props: {
      combinations,
      ...(await serverSideTranslations(locale, ['common', 'menus']))
    },
    // Revalidate every day to catch new combinations
    revalidate: 86400
  };
}